import { Controller } from "@hotwired/stimulus";
import {
  applyParticipantLaborUnits as fillParticipantLaborUnits,
  reorderParticipantRows,
} from "../helpers/assignment_participants.js";
import { currentFieldTaskId } from "../helpers/line_item_form.js";
import { newNameIn } from "../helpers/combobox_new_name.js";

export default class extends Controller {
  static targets = [
    "rowContainer",
    "template",
    "row",
    "costMethodInput",
    "contractorRatePrompts",
  ];
  static values = {
    farmLaborRate: String,
    taskPrices: Object,
    laborDayTaskIds: Array,
    taskDefaultCostMethods: Object,
    zeroPriceTaskIds: Array,
    contractorGroupMemberIds: Object,
    contractorLaborRates: Object,
  };

  connect() {
    this.#syncLaborDayCostMethodLock();
    this.rowTargets.forEach((row) => {
      row.dataset.assignmentTarget = this.#rowAssignmentTarget(row);
      this.#applyCostMethodToRow(row, this.#currentSectionMethod());
      this.#refreshAssignmentParticipants(row);
    });
    this.#refreshContractorRatePrompts();
  }

  // field-task-changed carries a new Field Task's meta (ADR 0090), or null for an existing one.
  fieldTaskChanged(event) {
    if (event?.type === "field-task-changed") this.newTask = event.detail || null;
    this.#applyTaskDefaultCostMethod();
    this.#syncLaborDayCostMethodLock();
    this.rowTargets.forEach((row) => {
      this.#applyCostMethodToRow(row, this.#currentSectionMethod());
      this.#refreshAssignmentParticipants(row);
    });
    this.#refreshContractorRatePrompts();
  }

  quantityChanged(event) {
    if (this.#currentSectionMethod() !== "labor_unit") return;
    if (!this.#currentTaskIsLaborDay()) return;
    const row = event.target.closest("[data-line-item-rows-target='row']");
    if (!row) return;
    const laborUnitsInput = row.querySelector(
      "input[name$='[budgeted_labor_units]']",
    );
    if (laborUnitsInput) laborUnitsInput.value = event.target.value;
  }

  addRow() {
    const index = this.rowTargets.length;
    const templateContent = this.templateTarget.innerHTML;
    const newRow = templateContent.replaceAll("__INDEX__", index);

    this.rowContainerTarget.insertAdjacentHTML("beforeend", newRow);

    // Defer target-dependent work: Stimulus's MutationObserver needs a frame
    // to register the newly-inserted [data-line-item-rows-target='row'] element.
    requestAnimationFrame(() => {
      const added = this.rowTargets[this.rowTargets.length - 1];
      this.#applyCostMethodToRow(added, this.#currentSectionMethod());
      this.#refreshAssignmentParticipants(added);
      this.#refreshContractorRatePrompts();
    });
  }

  duplicateRow(event) {
    const source = event.target.closest("[data-line-item-rows-target='row']");
    if (!source) return;

    const clone = source.cloneNode(true);
    this.#copyLiveFormState(source, clone);
    const lotSelect = clone.querySelector("select[name$='[lot_id]']");
    if (lotSelect) lotSelect.value = "";
    // Until reindexed, the clone's radios share the source's group and inserting it would uncheck one.
    const radioStates = Array.from(source.querySelectorAll("input[type='radio']"), (radio) => radio.checked);

    source.after(clone);
    this.reindexRows();
    [source, clone].forEach((row) => {
      row.querySelectorAll("input[type='radio']").forEach((radio, index) => {
        radio.checked = radioStates[index];
      });
    });

    requestAnimationFrame(() => {
      this.#refreshContractorRatePrompts();
      clone.querySelector("select[name$='[lot_id]']")?.focus();
    });
  }

  syncCostMethod(event) {
    const method = event.target.value;
    this.costMethodInputTargets.forEach((radio) => {
      radio.checked = radio.value === method;
    });
    this.rowTargets.forEach((row) => {
      this.#applyCostMethodToRow(row, method);
      this.#refreshAssignmentParticipants(row);
    });
    this.#refreshContractorRatePrompts();
  }

  // new-assignee:change fires on every change of a row's picker, and as it connects: only a new pick resets the row.
  contractorChanged(event) {
    // The previous contractor's typed rate must not carry over to the new one.
    // Scope the clear to the row whose contractor picker fired the event so other
    // rows keep the rates the user already typed for their own contractors.
    const row = event?.target?.closest?.("[data-line-item-rows-target='row']");
    if (row) {
      const target = this.#rowAssignmentTarget(row);
      if (row.dataset.assignmentTarget === target) return;
      row.dataset.assignmentTarget = target;
      if (event?.detail?.connecting) {
        this.#applyCostMethodToRow(row, this.#currentSectionMethod());
        this.#refreshAssignmentParticipants(row);
        this.#refreshContractorRatePrompts();
        return;
      }

      const rateHidden = row.querySelector("input[data-row-labor-rate]");
      const saveHidden = row.querySelector("input[data-row-save-contractor-rate]");
      const newRate = this.contractorLaborRatesValue[target] || "";
      if (rateHidden) rateHidden.value = newRate;
      if (saveHidden) saveHidden.value = "";
      this.#applyCostMethodToRow(row, this.#currentSectionMethod());
      this.#refreshAssignmentParticipants(row, true);
    }
    this.#refreshContractorRatePrompts();
  }

  syncContractorRate(event) {
    const input = event.target;
    const contractorId = input.dataset.contractorId;
    const value = input.value.trim();

    this.rowTargets.forEach((row) => {
      if (this.#rowContractorId(row) !== contractorId) return;
      const rateHidden = row.querySelector("input[data-row-labor-rate]");
      const saveHidden = row.querySelector("input[data-row-save-contractor-rate]");
      if (rateHidden) rateHidden.value = value;
      if (saveHidden) saveHidden.value = value.length > 0 ? "1" : "";
    });
  }

  removeRow(event) {
    const row = event.target.closest("[data-line-item-rows-target='row']");
    if (!row) return;
    if (this.rowTargets.length <= 1) return;

    row.remove();
    this.reindexRows();
    this.#refreshContractorRatePrompts();
    this.dispatch("lots-changed");
  }

  applyParticipantLaborUnits(event) {
    const panel = event.target.closest("[data-assignment-participants]");
    if (!panel) return;

    fillParticipantLaborUnits(panel);
  }

  toggleDates(event) {
    const row = event.target.closest("[data-line-item-rows-target='row']");
    if (!row) return;

    const datesSection = row.querySelector("[data-dates-override]");
    if (!datesSection) return;

    const isOpening = datesSection.classList.contains("hidden");
    datesSection.classList.toggle("hidden");

    if (isOpening) {
      const sharedCheckboxes = document.querySelectorAll(
        'input[name="field_cycle_line_item[scheduled_dates][]"]:checked',
      );
      const selectedDates = new Set(
        Array.from(sharedCheckboxes).map((cb) => cb.value),
      );
      datesSection.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = selectedDates.has(cb.value);
      });
    } else {
      datesSection.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });
    }
  }

  // Field names, and the names and ids the row's combobox and new-person template carry, follow the row's position.
  reindexRows() {
    this.rowTargets.forEach((row, index) => {
      const fields = [row, ...Array.from(row.querySelectorAll("template"), (template) => template.content)];
      fields.forEach((scope) => {
        scope.querySelectorAll("[name]").forEach((input) => {
          input.name = input.name.replace(/\[line_items\]\[\d+\]/, `[line_items][${index}]`);
        });
      });
      row.querySelectorAll("[data-hw-combobox-name-when-new-value]").forEach((fieldset) => {
        ["hwComboboxNameWhenNewValue", "hwComboboxOriginalNameValue"].forEach((key) => {
          fieldset.dataset[key] = fieldset.dataset[key].replace(/\[line_items\]\[\d+\]/, `[line_items][${index}]`);
        });
      });
    });
  }

  // --- private helpers ---------------------------------------------------

  // Browsers copy live input state on cloneNode; happy-dom (tests) drops checkbox
  // and select state, so mirror it explicitly.
  #copyLiveFormState(source, clone) {
    const sourceFields = source.querySelectorAll("input, select, textarea");
    const cloneFields = clone.querySelectorAll("input, select, textarea");
    sourceFields.forEach((field, index) => {
      const dest = cloneFields[index];
      if (!dest) return;
      if (field.type === "checkbox" || field.type === "radio") {
        dest.checked = field.checked;
      } else {
        dest.value = field.value;
      }
    });
  }

  #currentSectionMethod() {
    if (!this.hasCostMethodInputTarget) return "task_unit";
    const checked = this.costMethodInputTargets.find((r) => r.checked);
    return checked?.value || "task_unit";
  }

  #applyCostMethodToRow(row, method) {
    const hidden = row.querySelector("input[data-row-cost-method]");
    if (hidden) hidden.value = method;

    const isLaborDay = this.#currentTaskIsLaborDay();
    const hasGroupAssignment = this.#rowContractorGroupId(row).length > 0;
    const showLaborUnits =
      method === "labor_unit" && !isLaborDay && !hasGroupAssignment;
    const hideQuantity =
      method === "labor_unit" && isLaborDay && hasGroupAssignment;

    const quantitySection = row.querySelector("[data-quantity-section]");
    if (quantitySection) {
      quantitySection.classList.toggle("hidden", hideQuantity);
      const quantityInput = quantitySection.querySelector("input[data-quantity]");
      if (quantityInput) {
        quantityInput.required = !hideQuantity;
        if (hideQuantity && !quantityInput.value) {
          quantityInput.value = "1";
        }
      }
    }

    const laborUnitsSection = row.querySelector("[data-labor-units-section]");
    if (laborUnitsSection) {
      laborUnitsSection.classList.toggle("hidden", !showLaborUnits);
      const laborUnitsInput = laborUnitsSection.querySelector("input");
      if (laborUnitsInput) {
        laborUnitsInput.required = showLaborUnits;
        if (!showLaborUnits && (method !== "labor_unit" || hasGroupAssignment)) {
          laborUnitsInput.value = "";
        }
      }
    }

    // When the field task itself is measured in jornales/days/hours and the
    // user picks labor_unit (solo), mirror the Cantidad value into the
    // budgeted_labor_units input so the server gets a value to validate.
    if (method === "labor_unit" && isLaborDay && !hasGroupAssignment) {
      const quantityInput = row.querySelector("input[data-quantity]");
      const laborUnitsInput = row.querySelector(
        "input[name$='[budgeted_labor_units]']",
      );
      if (quantityInput && laborUnitsInput) {
        laborUnitsInput.value = quantityInput.value;
      }
    }

    // Clear any rate the user previously typed when leaving labor_unit so it
    // doesn't post a stale value.
    if (method !== "labor_unit") {
      const rateHidden = row.querySelector("input[data-row-labor-rate]");
      const saveHidden = row.querySelector("input[data-row-save-contractor-rate]");
      if (rateHidden) rateHidden.value = "";
      if (saveHidden) saveHidden.value = "";
    }

    // Show the price prompt only when costing by task_unit AND the selected
    // task has no default_price. Open assignment has no planned cost.
    const showPricePrompt =
      method === "task_unit" &&
      this.#currentTaskHasZeroPrice() &&
      !this.#rowIsOpenAssignment(row);
    const pricePromptSection = row.querySelector(
      "[data-price-prompt-section]",
    );
    if (pricePromptSection) {
      pricePromptSection.classList.toggle("hidden", !showPricePrompt);
      const priceInput = pricePromptSection.querySelector("input");
      if (priceInput) priceInput.required = showPricePrompt && Boolean(this.newTask);
      if (!showPricePrompt && priceInput) priceInput.value = "";
    }

    this.#refreshParticipantLaborControls(row);
  }

  #applyTaskDefaultCostMethod() {
    if (!this.hasCostMethodInputTarget) return;
    if (!this.hasTaskDefaultCostMethodsValue && !this.newTask) return;
    const taskId = this.#currentFieldTaskId();
    if (!taskId && !this.newTask) return;
    const defaultMethod = this.newTask
      ? this.newTask.costMethod
      : this.taskDefaultCostMethodsValue[String(taskId)];
    if (!defaultMethod) return;
    this.costMethodInputTargets.forEach((radio) => {
      radio.checked = radio.value === defaultMethod;
    });
  }

  // Jornal Field Tasks only allow jornal costing — hide/disable task_unit and force labor_unit.
  #syncLaborDayCostMethodLock() {
    if (!this.hasCostMethodInputTarget) return;

    const laborDay = this.#currentTaskIsLaborDay();
    this.costMethodInputTargets.forEach((radio) => {
      if (radio.value !== "task_unit") return;

      radio.disabled = laborDay;
      const label = radio.closest("label");
      if (label) {
        label.classList.toggle("hidden", laborDay);
        label.classList.toggle("cursor-pointer", !laborDay);
        label.classList.toggle("cursor-default", laborDay);
      }
    });

    if (laborDay && this.#currentSectionMethod() !== "labor_unit") {
      this.costMethodInputTargets.forEach((radio) => {
        radio.checked = radio.value === "labor_unit";
      });
    }
  }

  // A new Field Task has no price yet: every per-unit row asks for it.
  #currentTaskHasZeroPrice() {
    if (this.newTask) return true;
    if (!this.hasZeroPriceTaskIdsValue) return false;
    const id = this.#currentFieldTaskId();
    if (!id) return false;
    return this.zeroPriceTaskIdsValue.map(String).includes(id);
  }

  #currentFieldTaskId() {
    return currentFieldTaskId(this.element.closest("form") || document);
  }

  #currentTaskIsLaborDay() {
    if (this.newTask) return this.newTask.laborDay;
    if (!this.hasLaborDayTaskIdsValue) return false;
    const id = this.#currentFieldTaskId();
    if (!id) return false;
    return this.laborDayTaskIdsValue.map(String).includes(id);
  }

  #refreshContractorRatePrompts() {
    if (!this.hasContractorRatePromptsTarget) return;
    const panel = this.contractorRatePromptsTarget;

    const method = this.#currentSectionMethod();
    const usedContractorIds = new Set(
      this.rowTargets.map((row) => this.#rowContractorId(row)).filter(Boolean),
    );

    let anyVisible = false;
    panel.querySelectorAll("[data-contractor-rate-prompt]").forEach((prompt) => {
      const id = prompt.dataset.contractorId;
      const shouldShow = method === "labor_unit" && usedContractorIds.has(id);
      prompt.classList.toggle("hidden", !shouldShow);
      if (shouldShow) anyVisible = true;
    });

    panel.classList.toggle("hidden", !anyVisible);
    // We intentionally do NOT clear per-row labor rates or visible prompt
    // inputs when the panel hides: a transient toggle to task_unit and back
    // should preserve user-typed rates. #applyCostMethodToRow already clears
    // rates when method actually leaves labor_unit on a per-row basis;
    // contractorChanged handles contractor swaps explicitly.
  }

  // The row's assignee as the picker submits it ("contractor:3", "contractor_group:2", "open"). A new name answered
  // as a contractor or a group is "contractor:new" or "contractor_group:new": no rate or members yet.
  #rowAssignmentTarget(row) {
    const picker = row.querySelector("[data-controller~='new-assignee']");
    if (picker && newNameIn(picker)) {
      const kind = picker.querySelector("input[name$='[new_assignee_kind]']:checked")?.value;
      return kind ? `${kind}:new` : "";
    }

    return row.querySelector("input[name$='[assignment_target]']")?.value || "";
  }

  #rowIsOpenAssignment(row) {
    return this.#rowAssignmentTarget(row) === "open";
  }

  #rowContractorId(row) {
    const target = this.#rowAssignmentTarget(row);
    return target.startsWith("contractor:") ? target.replace("contractor:", "") : "";
  }

  #refreshAssignmentParticipants(row, resetSelection = false) {
    const panel = row.querySelector("[data-assignment-participants]");
    if (!panel) return;

    const groupId = this.#rowContractorGroupId(row);
    const show = groupId.length > 0;
    panel.classList.toggle("hidden", !show);
    panel.open = show;
    this.#refreshParticipantLaborControls(row);
    this.#refreshQuantitySection(row);

    if (!show) {
      panel.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
        checkbox.checked = false;
      });
      return;
    }

    const memberIds = new Set(
      (this.contractorGroupMemberIdsValue[String(groupId)] || []).map(String),
    );
    const explicitSelection = panel.dataset.participantSelectionExplicit === "true";
    if (resetSelection || !explicitSelection) {
      panel.querySelectorAll("[data-participant-row]:not([data-new-person-row]) input[type='checkbox']").forEach((checkbox) => {
        checkbox.checked = memberIds.has(checkbox.value);
      });
    }
    reorderParticipantRows(panel, memberIds);
  }

  #refreshQuantitySection(row) {
    const method = this.#currentSectionMethod();
    const isLaborDay = this.#currentTaskIsLaborDay();
    const hasGroupAssignment = this.#rowContractorGroupId(row).length > 0;
    const hideQuantity =
      method === "labor_unit" && isLaborDay && hasGroupAssignment;

    const quantitySection = row.querySelector("[data-quantity-section]");
    if (!quantitySection) return;

    quantitySection.classList.toggle("hidden", hideQuantity);
    const quantityInput = quantitySection.querySelector("input[data-quantity]");
    if (quantityInput) {
      quantityInput.required = !hideQuantity;
      if (hideQuantity && !quantityInput.value) {
        quantityInput.value = "1";
      }
    }
  }

  #refreshParticipantLaborControls(row) {
    const panel = row.querySelector("[data-assignment-participants]");
    if (!panel) return;

    const method = this.#currentSectionMethod();
    const hasGroup = this.#rowContractorGroupId(row).length > 0;
    const showLaborControls = method === "labor_unit" && hasGroup;
    const showRateControls = method === "labor_unit" && hasGroup;

    panel.querySelectorAll("[data-participant-labor-controls]").forEach((element) => {
      element.classList.toggle("hidden", !showLaborControls);
    });
    panel.querySelectorAll("[data-participant-rate-controls]").forEach((element) => {
      element.classList.toggle("hidden", !showRateControls);
    });

    // Keep name | jornales | rate on one row when labor controls are visible.
    // Rows SSR'd under task_unit use sm:grid-cols-1; without this they stay stacked.
    const laborUnitGrid = "sm:grid-cols-[minmax(0,1fr)_7rem_8rem]";
    panel.querySelectorAll("[data-participant-row]").forEach((participantRow) => {
      participantRow.classList.toggle("sm:grid-cols-1", !showLaborControls);
      participantRow.classList.toggle(laborUnitGrid, showLaborControls);
    });
  }

  #rowContractorGroupId(row) {
    const target = this.#rowAssignmentTarget(row);
    return target.startsWith("contractor_group:") ? target.replace("contractor_group:", "") : "";
  }
}
